import { Theme, css, useTheme } from '@emotion/react';

import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { Suspense } from 'react';
import { useQuery } from 'react-query';

import { OverlayPopOption } from '@stores/useOverlayStore';
import { Spacing, Typography } from 'chatie-design-system';
import { v4 as uuidv4 } from 'uuid';

import Button from '@components/common/Button';
import Loading from '@components/common/Loading';

import { fetchRecommendPaymentItems } from '@api/common';
import { PAYMENT_VENDOR, Platform, PurchaseType, QUERY_KEYS } from '@constants';
import { useGlobalContext } from '@contexts/global';
import { IDisplayPreset } from '@model';
import { useAnalytics } from '@utils/analytics';
import { useMe } from '@utils/hook';
import {
  checkRequiredAppVersion,
  formatComma,
  openDeepLink,
} from '@utils/misc';
import { useWebView } from '@utils/webview';

import Modal, { ModalProps } from '../common/Modal';

const legacyContainerCSS = css`
  text-align: center;
  ${Typography.body14};

  .highlight {
    background: var(
      --promotion-gra,
      linear-gradient(57deg, #ff07f5 19.7%, #ed0064 80.3%)
    );
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  .confirm-btn {
    margin-top: ${Spacing.xlarge}px;
  }
`;

const containerCSS = (isCheming?: boolean) => (theme: Theme) =>
  css`
    text-align: center;
    ${Typography.body14};

    button {
      box-shadow: none;
    }

    .button-container {
      display: flex;
      gap: ${Spacing.medium}px;
      margin-top: ${Spacing.xlarge}px;
    }

    .highlight {
      color: ${isCheming ? '#8E2DE2' : '#ed0064'};
    }

    .balloon {
      display: flex;
      justify-content: center;
      align-items: center;

      ${Typography.h5};
    }

    .bold {
      font-weight: 600;
    }

    .gray {
      ${Typography.body11};
      color: ${theme.var.gray500};
      margin-left: 6px;
      margin-right: 4px;
    }

    .main {
      ${Typography.h5};
      color: ${theme.colors.main500};
    }
    .vertical {
      flex-direction: column;
      margin-bottom: 6px;
    }
    .light-font {
      font-weight: 400;
    }
  `;

const ModalContent = ({
  isCheming,
  chatRoomId,
  onClose,
}: {
  isCheming?: boolean;
  chatRoomId?: number;
  onClose: (options?: OverlayPopOption | undefined) => void;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const uuid = uuidv4();
  const { data: me } = useMe();
  const { platform, version, goodsName } = useGlobalContext();
  const { firebase } = useAnalytics();
  const { web } = useWebView();
  const router = useRouter();

  const vendor =
    platform === Platform.ios ? PAYMENT_VENDOR.apple : PAYMENT_VENDOR.standard;
  const { data: recommendItems } = useQuery(
    QUERY_KEYS.PAYMENT.RECOMMEND(vendor),
    () => fetchRecommendPaymentItems(vendor)
  );

  const paymentItem = recommendItems?.personalRecommendItem;
  const hasPrevCharge = paymentItem?.purchaseType !== PurchaseType.OFP;
  const isRecommendItem = !!!recommendItems?.isPersonalRecommended;

  const calculatePrice = () => {
    if (!paymentItem) {
      return 0;
    }

    return (
      Math.floor(paymentItem.price / ((1 - paymentItem.discountRatio) * 100)) *
      100
    );
  };

  const beforeDiscount = calculatePrice();
  const discountPercent =
    (paymentItem && paymentItem?.discountRatio * 100) || 0;

  const handleOnConfirm = () => {
    if (paymentItem) {
      firebase.logEvent('payment_modal_btn_click', {
        group: 'B',
        btn_type: '바로충전',
        purchase_type: paymentItem.purchaseType,
        is_default_item: !recommendItems?.isPersonalRecommended,
      });

      if (platform === Platform.web) {
        let url = `/payment?payment_item_id=${paymentItem?.id}`;
        if (chatRoomId) {
          const backUrl = `/play/chat-rooms/${chatRoomId}?mode=continue`;
          url += `&prev=${backUrl}`;
        }
        onClose();
        setTimeout(() => {
          router.push(url);
        }, 300);
        return;
      }
      if (
        checkRequiredAppVersion(platform, version, {
          iOS: '2.26.1',
          android: '2.32.4',
        })
      ) {
        const uuidParam = uuid ? encodeURIComponent(uuid) : '';
        const prevPathParam = window?.location?.href
          ? encodeURIComponent(window?.location?.href)
          : '';

        return web.openPage(
          `/payment?payment_item_id=${paymentItem?.id}&prev_path=${prevPathParam}&uuid=${uuidParam}`
        );
      } else {
        return openDeepLink(
          `chatie-app://payment?payment_item_id=${paymentItem?.id}`
        );
      }
    }
  };

  const handleRoutePayment = () => {
    if (paymentItem) {
      firebase.logEvent('payment_modal_btn_click', {
        group: 'B',
        btn_type: '충전소',
        purchase_type: paymentItem.purchaseType,
        is_default_item: !recommendItems?.isPersonalRecommended,
      });
    }
    if (platform === Platform.web) {
      let url = `/payment?payment_item_id=${paymentItem?.id}`;
      if (chatRoomId) {
        const backUrl = `/play/chat-rooms/${chatRoomId}?mode=continue`;
        url += `&prev=${backUrl}`;
      }
      onClose();
      setTimeout(() => {
        router.push(url);
      }, 300);
      return;
    }
    if (
      checkRequiredAppVersion(platform, version, {
        iOS: '2.26.1',
        android: '2.32.4',
      })
    ) {
      return web.openPage('/payment');
    } else {
      return openDeepLink(`chatie-app://payment`);
    }
  };

  return (
    <div css={containerCSS(isCheming)}>
      {hasPrevCharge ? (
        <>
          <p className="multiline">{t(`${goodsName}이 부족해요 🥲`)}</p>

          {isRecommendItem ? (
            <>
              <p className="multiline">{t('\n최근 7일간 가장 인기있는')}</p>
              <p className="multiline highlight">
                {t(
                  `${paymentItem?.description} (${formatComma(
                    paymentItem?.price || 0
                  )}원)`
                )}
              </p>
              <p className="multiline">
                {t(`${me?.nickname}님께 추천드려요!`)}
              </p>
            </>
          ) : (
            <>
              <p className="multiline">
                {t('이전에 ')}
                <span className="highlight">
                  {t(
                    `${paymentItem?.quantity}${goodsName} (${paymentItem?.price}원)`
                  )}
                </span>
                {t('을 충전하셨네요.\n이번에도 같은 금액을 충전할까요?')}
              </p>
            </>
          )}

          <div className="button-container">
            <Button
              text={t('충전소 가기')}
              onClick={handleRoutePayment}
              backgroundColor={
                isCheming ? theme.colors.main500 : theme.var.gray200
              }
              color={isCheming ? theme.var.white : theme.var.gray500}
            />
            {!isCheming && (
              <Button text={t('빠른 충전하기')} onClick={handleOnConfirm} />
            )}
          </div>
        </>
      ) : (
        <>
          <p className="multiline">{t(`${goodsName}이 부족해요.\n\n`)}</p>
          <p className="multiline">
            {t(
              `${me?.nickname}님, ${goodsName}을 처음 충전하시네요 ${
                isCheming ? '🔮' : '🎈'
              }`
            )}
          </p>
          <p className="multiline">
            {t('지금 충전하면 ')}
            <span className="highlight bold">
              {t(`${discountPercent}% 할인`)}
            </span>
            {t('을 받을 수 있어요!\n\n')}
          </p>
          <p className="multiline balloon">
            {t(`${paymentItem?.description}`)}
            <del className="gray">{t(`${formatComma(beforeDiscount)}원`)}</del>
            <span className="main">
              {t(`${formatComma(paymentItem?.price || 0)}원`)}
            </span>
          </p>

          <div className="button-container vertical">
            {!isCheming && (
              <Button
                text={t('지금 할인 받을래요!')}
                onClick={handleOnConfirm}
              />
            )}
            {!isCheming && (
              <Button
                className="light-font"
                text={t('충전소 가기')}
                onClick={handleRoutePayment}
                type={'text'}
              />
            )}
            {isCheming && (
              <Button
                text={t('충전소 가기')}
                onClick={handleRoutePayment}
                backgroundColor={
                  isCheming ? theme.colors.main500 : theme.var.gray200
                }
                color={isCheming ? theme.var.white : theme.var.gray500}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

type ChargeBalloonModalProps = {
  isGuestChatRoom?: boolean;
  selectedPreset?: IDisplayPreset;
  isCheming?: boolean;
  chatRoomId?: number;
};

const ChargeBalloonModal = ({
  isGuestChatRoom,
  chatRoomId,
  selectedPreset,
  ...props
}: ModalProps & ChargeBalloonModalProps) => {
  const { open, onClose } = props;
  const { t } = useTranslation();
  const { goodsName, isCheming } = useGlobalContext();

  const handleRouteLegacy = () => {
    window.parent.postMessage(
      {
        type: 'Charge',
        error: 'ChatieErrorCode.Charge',
      },
      '*'
    );
  };

  if (!open) {
    return <></>;
  }

  if (isGuestChatRoom) {
    return (
      <Modal
        {...props}
        blocking={true}
        onClose={onClose}
        header
        title={`${goodsName}이 부족해요`}
      >
        <div css={legacyContainerCSS}>
          <p className="multiline">
            {t(
              `${goodsName}이 부족해요 🥲\n캐릭터와 대화를 이어나려면 ${goodsName}이 필요해요.`
            )}
          </p>
          {selectedPreset && (
            <span className="multiline highlight">
              {t(
                `\n1대화당 ${
                  selectedPreset?.discount_message_price ??
                  selectedPreset?.message_price
                }${goodsName}`
              )}
            </span>
          )}
          <Button
            className="confirm-btn"
            text={t('지금 할인 받을래요!')}
            onClick={handleRouteLegacy}
          />
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      {...props}
      blocking={true}
      onClose={onClose}
      header
      title={`${goodsName}이 부족해요`}
    >
      <Suspense fallback={<Loading center />}>
        <ModalContent
          isCheming={isCheming}
          chatRoomId={chatRoomId}
          onClose={onClose}
        />
      </Suspense>
    </Modal>
  );
};

export default ChargeBalloonModal;
